// /* eslint-disable react/jsx-no-constructed-context-values */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { api } from "../services/api";
import { usePermissions } from "../hooks/usePermissions";
import { Permission } from "../types/permissions";

interface AuthProviderProps {
  children: ReactNode;
}

interface User {
  id: string;
  avatar_url: string;
  name: string;
  email: string;
  isAdmin: boolean;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  permissions: Permission[];
  hasPermission: (permissionName: string | string[]) => boolean;
  hasAllPermissions: (permissionNames: string[]) => boolean;
  permissionsLoading: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
  refetchPermissions: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
); // Burlar a typagem com o "as AuthContext"

export function AuthProvider({ children }: AuthProviderProps) {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem("@LuviApp:token");
    const user = localStorage.getItem("@LuviApp:user");

    if (token && user) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  // Hook de permissões
  const {
    permissions,
    hasPermission,
    hasAllPermissions,
    loading: permissionsLoading,
    refetch: refetchPermissions
  } = usePermissions();

  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post("sessions", {
      email,
      password,
    });

    const { token, user } = response.data;

    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("@LuviApp:token", token);
    localStorage.setItem("@LuviApp:user", JSON.stringify(user));

    setData({ token, user });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("@LuviApp:token");
    localStorage.removeItem("@LuviApp:user");

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback((user: User) => {
    localStorage.setItem("@LuviApp:user", JSON.stringify(user));
    setData(oldData => ({
      token: oldData.token,
      user,
    }));
  }, []);

  return (
    <AuthContext.Provider
      // value={{ user: data.user, signIn, signOut }}
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
        permissions,
        hasPermission,
        hasAllPermissions,
        permissionsLoading,
        refetchPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }

  return context;
}
